import React, { useEffect, useId, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
type TreatedImageProps = {
  src: string;
  alt: string;
  className?: string;
  /** Lift the treatment and zoom slightly when the parent `.group` is hovered */
  interactive?: boolean;
  /** CSS mask-image limiting a gentle "wind" sway to part of the photo (e.g. trees) */
  motionMask?: string;
  /** "foliage" = leafy canopy sway; "branches" = light bare-branch drift; "water" = rippling surface */
  motionStyle?: "foliage" | "branches" | "water";
  /** Load immediately (above-the-fold images); otherwise lazy-loaded */
  eager?: boolean;
};
const SMOOTH = "0.45 0 0.55 1";

/**
 * Two-layer wind: a slow, mostly horizontal sway of the whole canopy that
 * travels across it in gusts, plus a quick, small flutter of individual leaves.
 * The noise fields slide rather than morph, so it reads as motion, not boiling.
 */
function FoliageFilter({
  id
}: {
  id: string;
}) {
  return <filter id={id} x="-20%" y="-20%" width="140%" height="140%" colorInterpolationFilters="sRGB" data-id="element-307" data-mp="iwoe0mc8ppt2">
      {/* Canopy sway */}
      <feTurbulence type="fractalNoise" baseFrequency="0.0045 0.009" numOctaves="2" seed="7" result="swayNoise" data-id="element-308" data-mp="nwklivk5bzdd" />
      <feColorMatrix in="swayNoise" type="matrix" values="1 0 0 0 0  0 0.3 0 0 0.35  0 0 0 0 0  0 0 0 0 1" result="swayBias" data-id="element-309" data-mp="st1a1qv9cfqz" />
      <feOffset in="swayBias" dx="0" dy="0" result="sway" data-id="element-310" data-mp="11nfnrp053i7z">
        <animate attributeName="dx" dur="6s" values="-18;22;-6;16;-18" keyTimes="0;0.32;0.55;0.8;1" calcMode="spline" keySplines={`${SMOOTH};${SMOOTH};${SMOOTH};${SMOOTH}`} repeatCount="indefinite" data-id="element-311" data-mp="11p0knl9xfd90" />
        <animate attributeName="dy" dur="7.5s" values="0;5;-3;0" keyTimes="0;0.4;0.75;1" calcMode="spline" keySplines={`${SMOOTH};${SMOOTH};${SMOOTH}`} repeatCount="indefinite" data-id="element-312" data-mp="1gjolgm4dtnia" />
      </feOffset>
      <feDisplacementMap in="SourceGraphic" in2="sway" scale="14" xChannelSelector="R" yChannelSelector="G" result="swayed" data-id="element-313" data-mp="3abwyslosccdo">
        <animate attributeName="scale" dur="11s" values="10;16;11;18;10" keyTimes="0;0.25;0.5;0.8;1" calcMode="spline" keySplines={`${SMOOTH};${SMOOTH};${SMOOTH};${SMOOTH}`} repeatCount="indefinite" data-id="element-314" data-mp="35dc0wsx0a4se" />
      </feDisplacementMap>

      {/* Leaf flutter */}
      <feTurbulence type="fractalNoise" baseFrequency="0.06" numOctaves="1" seed="2" result="flutterNoise" data-id="element-315" data-mp="3oamwo3jfig33" />
      <feOffset in="flutterNoise" dx="0" dy="0" result="flutter" data-id="element-316" data-mp="6tvlvy4gobgn">
        <animate attributeName="dx" dur="1.4s" values="0;3;-2;0" repeatCount="indefinite" data-id="element-317" data-mp="6tdjskv4b30t" />
        <animate attributeName="dy" dur="1.9s" values="0;-2;2;0" repeatCount="indefinite" data-id="element-318" data-mp="1vu2fc34ia4n" />
      </feOffset>
      <feDisplacementMap in="swayed" in2="flutter" scale="4" xChannelSelector="R" yChannelSelector="B" data-id="element-319" data-mp="fsqrb7zolhiw" />
    </filter>;
}

/**
 * Water: noise stretched into long horizontal bands (like ripples seen at a
 * low angle) that drift slowly toward the viewer, wobbling reflections
 * mostly side to side. A finer second layer adds surface shimmer.
 */
function WaterFilter({
  id
}: {
  id: string;
}) {
  return <filter id={id} x="-10%" y="-30%" width="120%" height="160%" colorInterpolationFilters="sRGB" data-id="element-320" data-mp="liyfo6lkjwtj">
      {/* Broad swells */}
      <feTurbulence type="fractalNoise" baseFrequency="0.0035 0.045" numOctaves="2" seed="11" result="swellNoise" data-id="element-321" data-mp="brapsa4nunu6" />
      <feOffset in="swellNoise" dx="0" dy="0" result="swell" data-id="element-322" data-mp="gi0tzk1cokna">
        <animate attributeName="dy" dur="7s" values="-14;14;-14" keyTimes="0;0.5;1" calcMode="spline" keySplines={`${SMOOTH};${SMOOTH}`} repeatCount="indefinite" data-id="element-323" data-mp="gjlnd6bxhxjk" />
        <animate attributeName="dx" dur="13s" values="-20;20;-20" keyTimes="0;0.5;1" calcMode="spline" keySplines={`${SMOOTH};${SMOOTH}`} repeatCount="indefinite" data-id="element-324" data-mp="3oylx4k7awjih" />
      </feOffset>
      <feDisplacementMap in="SourceGraphic" in2="swell" scale="16" xChannelSelector="R" yChannelSelector="A" result="swelled" data-id="element-325" data-mp="w32itfx4se91" />

      {/* Surface shimmer */}
      <feTurbulence type="fractalNoise" baseFrequency="0.012 0.16" numOctaves="1" seed="5" result="shimmerNoise" data-id="element-326" data-mp="raswe9v85wbi" />
      <feOffset in="shimmerNoise" dx="0" dy="0" result="shimmer" data-id="element-327" data-mp="md0viqclqzv6">
        <animate attributeName="dy" dur="2.6s" values="0;5;0" repeatCount="indefinite" data-id="element-328" data-mp="hf90vokkozna" />
        <animate attributeName="dx" dur="3.4s" values="0;-6;0" repeatCount="indefinite" data-id="element-329" data-mp="cetph2juq27y" />
      </feOffset>
      <feDisplacementMap in="swelled" in2="shimmer" scale="5" xChannelSelector="R" yChannelSelector="G" data-id="element-330" data-mp="8ejax1hzu3hh" />
    </filter>;
}

// Fine film grain, generated inline so there's no extra asset
const GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

/**
 * Gives any photo the site's warm, slightly faded film look so mixed
 * sources sit together: softened saturation, a warm cream wash, a touch of
 * espresso in the shadows and fine grain. The image stays fully visible.
 */
const DESKTOP_QUERY = "(min-width: 768px)";
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isDesktop;
}
export function TreatedImage({
  src,
  alt,
  className = "",
  interactive = false,
  motionMask,
  motionStyle = "branches",
  eager = false
}: TreatedImageProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  // The wind filters are costly to composite, so they only run while the photo is on screen
  const inView = useInView(ref, {
    margin: "120px"
  });
  const filterId = `wind-${useId().replace(/:/g, "")}`;
  const isDesktop = useIsDesktop();
  // Tree and water motion is desktop-only: phones get the still, treated photo
  const showWind = Boolean(motionMask) && !reduce && inView && isDesktop;
  return <div ref={ref} className={`relative overflow-hidden ${className}`} data-id="element-331" data-mp="oyztap453tue">
      <div className={`relative h-full w-full [filter:saturate(0.78)_sepia(0.16)_contrast(1.04)_brightness(0.98)] ${interactive ? "transition-[filter,transform] duration-300 ease-out group-hover:scale-[1.04] group-hover:[filter:saturate(0.92)_sepia(0.08)_contrast(1.04)_brightness(1)] motion-reduce:group-hover:scale-100" : ""}`} data-id="element-332" data-mp="12pmqudy5zdow">
        <img src={src} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" className="h-full w-full object-cover" data-id="element-333" data-mp="2gwj7f2njs2jz" />

        {showWind && <>
            <svg aria-hidden="true" className="absolute h-0 w-0" data-id="element-334" data-mp="27xfmru8qff3d">
              {motionStyle === "foliage" ? <FoliageFilter id={filterId} data-id="element-335" data-mp="2cvz9vxz2awxb" /> : motionStyle === "water" ? <WaterFilter id={filterId} data-id="element-336" data-mp="30md4nw9179vl" /> : <filter id={filterId} x="-5%" y="-5%" width="110%" height="110%" data-id="element-337" data-mp="34n5g8w5dlqv1">
                  <feTurbulence type="fractalNoise" baseFrequency="0.014 0.03" numOctaves="2" seed="4" data-id="element-338" data-mp="2qmumnlcoly66">
                    <animate attributeName="baseFrequency" dur="9s" values="0.014 0.03;0.017 0.036;0.013 0.028;0.014 0.03" repeatCount="indefinite" data-id="element-339" data-mp="2vlp34gn6fan4" />
                  </feTurbulence>
                  <feDisplacementMap in="SourceGraphic" scale="6" xChannelSelector="R" yChannelSelector="G" data-id="element-340" data-mp="2bud8ec6tx648">
                    <animate attributeName="scale" dur="4.5s" values="3;8;4;9;3" keyTimes="0;0.3;0.5;0.75;1" repeatCount="indefinite" data-id="element-341" data-mp="26vjsye7zf51f" />
                  </feDisplacementMap>
                </filter>}
            </svg>
            <img src={src} alt="" aria-hidden="true" decoding="async" className="pointer-events-none absolute inset-0 h-full w-full object-cover" style={{
          filter: `url(#${filterId})`,
          WebkitMaskImage: motionMask,
          maskImage: motionMask
        }} data-id="element-342" data-mp="2uk318tr17hti" />
          </>}
      </div>

      {/* Warm highlight wash */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-cream opacity-[0.12] mix-blend-multiply" data-id="element-343" data-mp="38jkx1t2qvvcd" />
      {/* Warm dark-brown tone */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[#5a3d2a] opacity-[0.22] mix-blend-multiply" data-id="element-344" data-mp="3kuve0uxh4wl4" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-espresso opacity-[0.12] mix-blend-soft-light" data-id="element-345" data-mp="3ptotgswbmxnx" />
      {/* Film grain */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay" style={{
      backgroundImage: GRAIN
    }} data-id="element-346" data-mp="3uqyg60sbsv82" />
    </div>;
}